import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, HelpCircle, ExternalLink, Activity, BrainCircuit, Copy, Share2, Dna, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

export const AnalysisResult = ({ result }) => {
    const [isDnaExpanded, setIsDnaExpanded] = useState(true);
    
    if (!result) return null;

    const { verdict, trust_score, explanation, sources } = result;

    const getVerdictConfig = (v) => {
        const lowerV = v?.toLowerCase();
        if (lowerV?.includes('true')) return { color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: CheckCircle2, label: 'Verified True' };
        if (lowerV?.includes('false')) return { color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', icon: XCircle, label: 'Verified False' };
        if (lowerV?.includes('misleading')) return { color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', icon: AlertCircle, label: 'Misleading' };
        return { color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-700', icon: HelpCircle, label: 'Unverified' };
    };

    const config = getVerdictConfig(verdict);
    const Icon = config.icon;

    const handleCopy = () => {
        const text = `VeryTrust Analysis\nVerdict: ${verdict}\nScore: ${trust_score}%\n\n${explanation}`;
        navigator.clipboard.writeText(text);
        toast.success("Analysis copied to clipboard");
    };

    const handleShare = async () => {
        const text = `VeryTrust Analysis\nVerdict: ${verdict}\nScore: ${trust_score}%\n\n${explanation}`;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'VeryTrust Verification',
                    text: text,
                    url: window.location.href,
                });
            } catch (err) {
                console.error('Share failed:', err);
            }
        } else {
            handleCopy();
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
            {/* Verdict Card */}
            <div className={`md:col-span-1 p-6 rounded-2xl border ${config.border} ${config.bg} flex flex-col items-center justify-center text-center relative group`}>
                <Icon className={`w-16 h-16 mb-4 ${config.color}`} />
                <h2 className={`text-2xl font-bold ${config.color} mb-2`}>{config.label}</h2>
                <div className="w-full mt-4 space-y-2">
                    <div className="flex justify-between text-sm font-medium text-gray-600 dark:text-gray-300">
                        <span>Trust Score</span>
                        <span>{trust_score}%</span>
                    </div>
                    <Progress value={trust_score} className="h-2" />
                </div>
            </div>

            {/* Details Card */}
            <div className="md:col-span-2 bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 relative">
                <div className="absolute top-4 right-4 flex gap-2">
                    <button onClick={handleCopy} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 transition-colors" title="Copy Result">
                        <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={handleShare} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 transition-colors" title="Share">
                        <Share2 className="w-4 h-4" />
                    </button>
                </div>

                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" /> Analysis
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                    {explanation}
                </p>

                {sources && sources.length > 0 && (
                    <div>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                            References & Knowledge Base
                        </h4>
                        <div className="space-y-3">
                            {sources.map((source, idx) => {
                                const isInternal = source.link === "ai://internal-knowledge";
                                return (
                                    <a 
                                        key={idx} 
                                        href={isInternal ? "#" : source.link}
                                        target={isInternal ? "_self" : "_blank"}
                                        rel="noopener noreferrer"
                                        className={`flex items-start gap-3 p-3 rounded-lg transition-colors group ${isInternal ? 'bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 cursor-default' : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'}`}
                                        onClick={(e) => isInternal && e.preventDefault()}
                                    >
                                        <div className="mt-1 min-w-[16px]">
                                            {isInternal ? (
                                                <BrainCircuit className="w-4 h-4 text-primary" />
                                            ) : (
                                                <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-primary" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary line-clamp-1">
                                                {source.title}
                                            </div>
                                            <div className="text-xs text-gray-500 line-clamp-1">
                                                {isInternal ? "Large Language Model Consensus (OpenAI/Gemini)" : source.link}
                                            </div>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Hoax DNA Section - NEW */}
            {result.hoax_dna && (
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="md:col-span-3 bg-gradient-to-br from-purple-50 to-cyan-50 dark:from-gray-800/50 dark:to-gray-900/50 rounded-2xl border-2 border-purple-200 dark:border-purple-700 overflow-hidden"
                >
                    {/* Header */}
                    <button
                        onClick={() => setIsDnaExpanded(!isDnaExpanded)}
                        className="w-full p-5 flex items-center justify-between hover:bg-purple-100/30 dark:hover:bg-purple-900/20 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                                <Dna className="w-6 h-6 text-white animate-pulse" />
                            </div>
                            <div className="text-left">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                        🧬 Hoax DNA Fingerprint
                                    </h3>
                                    <span className="text-xs font-bold px-2 py-0.5 bg-purple-500 text-white rounded-full animate-pulse">NEW</span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Unique signature & similarity analysis</p>
                            </div>
                        </div>
                        {isDnaExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                    </button>

                    {/* Content */}
                    <AnimatePresence>
                        {isDnaExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-purple-200 dark:border-purple-700"
                            >
                                <div className="p-5 grid md:grid-cols-2 gap-4">
                                    {/* DNA Hash */}
                                    <div className="bg-white/60 dark:bg-gray-800/60 p-4 rounded-xl backdrop-blur-sm">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">DNA Hash</h4>
                                        </div>
                                        <p className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900 px-3 py-2 rounded-lg break-all">
                                            {result.hoax_dna.dna_hash}
                                        </p>
                                    </div>

                                    {/* Keywords */}
                                    <div className="bg-white/60 dark:bg-gray-800/60 p-4 rounded-xl backdrop-blur-sm">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Key Markers</h4>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {result.hoax_dna.keywords && result.hoax_dna.keywords.length > 0 ? (
                                                result.hoax_dna.keywords.map((keyword, idx) => (
                                                    <span 
                                                        key={idx}
                                                        className="px-2 py-1 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium border border-purple-300 dark:border-purple-600"
                                                    >
                                                        {keyword}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-xs text-gray-500">No keywords</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sentiment */}
                                    {result.hoax_dna.sentiment_markers && result.hoax_dna.sentiment_markers.length > 0 && (
                                        <div className="bg-white/60 dark:bg-gray-800/60 p-4 rounded-xl backdrop-blur-sm">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                                                <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Emotional Patterns</h4>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {result.hoax_dna.sentiment_markers.map((marker, idx) => (
                                                    <span 
                                                        key={idx}
                                                        className="px-2 py-1 bg-pink-500/20 text-pink-700 dark:text-pink-300 rounded-full text-xs font-medium capitalize"
                                                    >
                                                        {marker.replace('_', ' ')}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Similar Hoaxes */}
                                    <div className={`${result.hoax_dna.similar_hoaxes_count > 0 ? 'md:col-span-2' : ''}`}>
                                        {result.hoax_dna.similar_hoaxes_count > 0 ? (
                                            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-700">
                                                <div className="flex items-start gap-3">
                                                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-1 text-sm">
                                                            ⚠️ Similar Hoaxes Detected
                                                        </h4>
                                                        <p className="text-xs text-amber-800 dark:text-amber-200 mb-3">
                                                            Found <strong>{result.hoax_dna.similar_hoaxes_count}</strong> similar variant(s) in database
                                                        </p>
                                                        {result.hoax_dna.similar_hoaxes && result.hoax_dna.similar_hoaxes.length > 0 && (
                                                            <div className="space-y-2">
                                                                {result.hoax_dna.similar_hoaxes.slice(0, 2).map((similar, idx) => (
                                                                    <div key={idx} className="text-xs bg-white/50 dark:bg-gray-800/50 p-2 rounded">
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="font-medium">Similarity: {similar.similarity}%</span>
                                                                            <span className={`px-2 py-0.5 rounded text-xs ${
                                                                                similar.verdict === 'False' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                                                                            }`}>
                                                                                {similar.verdict}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-xl border border-green-200 dark:border-green-700">
                                                <p className="text-xs text-green-800 dark:text-green-200 flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    No similar hoax variants found - unique claim
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="md:col-span-2 pt-3 border-t border-purple-200 dark:border-purple-700">
                                        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                                            💡 Hoax DNA tracks how misinformation evolves across languages and platforms
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            )}
        </motion.div>
    );
};
